import { Component, OnChanges, OnInit } from '@angular/core';
import { DynamicFormComponent } from '@components/dynamic-form/dynamic-form.component';
import { Router } from '@angular/router';
import { UserService } from '@services/admin/user.service';
import { ToastrService } from 'ngx-toastr';
import { Registry } from '@/registry.service';
import { LoggerService } from '@services/logger.service';
import { BrandGoalsService } from '@services/data-source-tables/brand-goals.service';
import { BrandGoalsModel } from '@/models/brand-goals.model';
import { BrandGoalsFormSetting } from '@/form-settings/brand-goals-form-setting';
import { OptionSelect } from '@/models/option-select.model';
import { BigBuyersModel } from '@/models/tables/big-buyers.model';

@Component({
  selector: 'app-brand-goals-form',
  templateUrl: '../../../../../components/dynamic-form/dynamic-form.component.html',
})
export class BrandGoalsFormComponent extends DynamicFormComponent implements OnInit, OnChanges {
  private bigBuyers: OptionSelect[] = [];
  private brands: OptionSelect[] = [];
  selectedBigBuyers: BigBuyersModel[] = [];

  constructor(
    protected router: Router,
    protected userService: UserService,
    protected brandGoalsService: BrandGoalsService,
    protected toastr: ToastrService,
  ) {
    super(toastr, userService);

    this.setupMeta();
    this.submitButtonVisible = true;
    this.primaryButton = 'Salva';
    this.secondaryBottomButton = 'Chiudi';
  }

  ngOnInit(): void {
    if (this.data?.marca?.id) {
      this.brandGoalsService.getBigBuyers(this.data.marca.id).subscribe((bigBuyers) => {
        this.bigBuyers = bigBuyers.map((bigBuyer) => ({
          key: bigBuyer,
          value: bigBuyer.codiceGA + ' - ' + bigBuyer.descrizione,
        }));
        this.setupMeta();
        // Wait for form to be recreated after setupMeta
        setTimeout(() => {
          this.updateSelectedBigBuyers();
        }, 200);
      });
    } else {
      // If no marca, still setup meta but without bigBuyers
      this.setupMeta();
    }

    this.brandGoalsService.getAllBrands().subscribe((brands) => {
      this.brands = brands.map((brand) => ({
        key: brand,
        value: brand.descrizioneMarca,
      }));
      // Only setup meta if bigBuyers are already loaded, otherwise wait
      if (this.bigBuyers.length > 0 || !this.data?.marca?.id) {
        this.setupMeta();
      }
    });
  }

  ngOnChanges() {
    super.ngOnChanges();
    // Wait a bit for form to be fully initialized
    setTimeout(() => {
      this.updateSelectedBigBuyers();
    }, 100);
  }

  refreshForm() {
    super.refreshForm();
    // After form is refreshed, update selected big buyers if data exists
    if (this.data?.grandiAcquirenti && this.bigBuyers.length > 0) {
      setTimeout(() => {
        this.updateSelectedBigBuyers();
      }, 100);
    }
  }

  private updateSelectedBigBuyers() {
    if (!this.form || !this.data?.grandiAcquirenti || !Array.isArray(this.data.grandiAcquirenti)) {
      return;
    }

    if (this.bigBuyers.length === 0) {
      return;
    }

    const grandiAcquirentiControl = this.form.get('grandiAcquirenti');
    if (!grandiAcquirentiControl) {
      return;
    }

    // Map existing grandiAcquirenti to match the objects in options
    const selectedValues = this.data.grandiAcquirenti
      .map((existing: BigBuyersModel) => {
        // Find matching option from bigBuyers list by ID
        const matchingOption = this.bigBuyers.find((option) => {
          if (!option || !option.key) {
            return false;
          }
          // Compare IDs - handle both number and string types
          const optionId = option.key.id;
          const existingId = existing.id;
          return optionId === existingId || String(optionId) === String(existingId);
        });
        return matchingOption ? matchingOption.key : null;
      })
      .filter((value) => value !== null); // Remove null values

    if (selectedValues.length > 0) {
      grandiAcquirentiControl.setValue(selectedValues, { emitEvent: false });
      this.selectedBigBuyers = selectedValues;
    }
  }

  onGrandiAcquirentiChange(selectedValues: BigBuyersModel[]) {
    this.selectedBigBuyers = selectedValues || [];
  }

  onSubmit() {
    let brandGoalModel = <BrandGoalsModel>this.form.getRawValue();

    this.brandGoalsService.saveBrandGoal(brandGoalModel).subscribe({
      next: () => {
        this.toastr.success('Obiettivo marca salvato correttamente!');

        this.closeModalAndRefresh();
      },
      error: (error) => {
        LoggerService.error('saveBrandGoal failed!');
        LoggerService.error(error);

        this.toastr.error(`Errore durante il salvataggio dell'obiettivo marca.`);

        setTimeout(() => {
          Registry.getInstance().set('saveInProgress', false);
        }, 1000);
      },
      complete: () => {
        LoggerService.log('Request done');

        setTimeout(() => {
          Registry.getInstance().set('saveInProgress', false);
        }, 1000);
      },
    });
  }

  setupMeta(): void {
    this.meta = BrandGoalsFormSetting.setClassToCall('BrandGoalsFormComponent')
      .set('bigBuyers', this.bigBuyers)
      .set('brands', this.brands)
      .getMeta()
      .sort((a: any, b: any) => a.order - b.order);

    Registry.getInstance().set('BrandGoalsFormComponent', this);
  }

  protected closeModalAndRefresh() {
    Registry.getInstance().get('DialogBrandGoalsComponentModify').close();
    Registry.getInstance().get('BrandGoalsComponent').loadBrandGoals();
  }

  bottomButtonAction() {
    Registry.getInstance().get('DialogBrandGoalsComponentModify').close();
  }
}
